import useLocalStorage, { writeStorage } from '@rehooks/local-storage';
import React from 'react';
import { useRouterQuery } from './useRouterQuery';

/**
 * Single source of truth for dark mode.
 *
 * Reads `localStorage.theme` with three states:
 *   - `'dark'`  → force dark
 *   - `'light'` → force light
 *   - `null`    → auto (follow OS via prefers-color-scheme)
 *
 * Optional URL override `?themeMode=dark|light|auto` for testing.
 *
 * Applies the `dark` class to BOTH `<html>` and `<body>` so Tailwind's
 * class-mode `dark:` variants activate on every descendant regardless of
 * which root the consumer queries.
 */

type Theme = 'dark' | 'light' | null;

const DARK_CLASSES = ['dark', 'darkMode'];

function applyDark(isDark: boolean) {
  for (const cls of DARK_CLASSES) {
    document.documentElement.classList.toggle(cls, isDark);
    document.body.classList.toggle(cls, isDark);
  }
}

function normalizeOverride(raw: string | undefined | null): Theme | undefined {
  if (raw == null) return undefined;
  const v = raw.toLowerCase();
  if (v === 'dark') return 'dark';
  if (v === 'light') return 'light';
  if (v === 'auto') return null;
  return undefined;
}

export const useDarkMode = () => {
  const [query] = useRouterQuery();
  const [storedTheme] = useLocalStorage<Theme>('theme', null);

  const override = normalizeOverride(query.themeMode);
  const theme: Theme = override !== undefined ? override : storedTheme;

  // Stale legacy key from a previous version of this hook. Clear it once on
  // mount so it stops overriding AUTO behavior for users with old state.
  React.useEffect(() => {
    if (window.localStorage.getItem('lightMode') != null) {
      window.localStorage.removeItem('lightMode');
    }
  }, []);

  React.useEffect(() => {
    if (theme === 'dark') {
      applyDark(true);
      return;
    }
    if (theme === 'light') {
      applyDark(false);
      return;
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    applyDark(mql.matches);
    const onChange = (e: MediaQueryListEvent) => applyDark(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [theme]);

  // Cycle: auto (null) → dark → light → auto. Advance from the EFFECTIVE theme (what
  // the switch shows), not just storedTheme, so a URL override can't desync the cycle.
  const cycleTheme = () => {
    const next: Theme = theme === null ? 'dark' : theme === 'dark' ? 'light' : null;
    writeStorage<Theme>('theme', next);
  };

  return { theme, cycleTheme };
};
